export interface StrapiImage {
  id: number;
  documentId?: string;
  name?: string;
  alternativeText?: string;
  caption?: string;
  url: string;
  width?: number;
  height?: number;
  formats?: Record<string, any>;
}

export interface StrapiAuthor {
  id: number;
  documentId?: string;
  name: string;
  bio?: string;
  avatar?: StrapiImage | null;
}

export interface StrapiCategory {
  id: number;
  documentId?: string;
  name: string;
  slug: string;
}

export interface StrapiBlogPost {
  id: number;
  documentId?: string;
  title: string;
  slug: string;
  body: string;
  excerpt: string;
  published_date?: string;
  read_time?: string;
  meta_title?: string;
  meta_description?: string;
  featured_image?: StrapiImage | null;
  author?: StrapiAuthor | null;
  category?: StrapiCategory | null;
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
}

export interface StrapiPagination {
  page: number;
  pageSize: number;
  pageCount: number;
  total: number;
}

export interface StrapiListResponse<T> {
  data: T[];
  meta?: {
    pagination?: StrapiPagination;
  };
}

export interface StrapiSingleResponse<T> {
  data: T | null;
}

const STRAPI_BASE_URL =
  (typeof window === 'undefined' && (process.env.INTERNAL_STRAPI_URL || process.env.STRAPI_INTERNAL_URL)) ||
  process.env.NEXT_PUBLIC_STRAPI_URL ||
  'http://localhost:1337';

/**
 * Returns a public, browser-safe media URL for Strapi assets.
 * - Relative paths (e.g. /uploads/image.jpg) are resolved against the public Strapi URL.
 * - Internal Docker addresses (e.g. http://strapi:1337) are rewritten to the public Strapi URL.
 * - Absolute URLs pointing to cms.astropredict.org with http:// are upgraded to https://.
 * - Null, undefined, or empty strings return null.
 */
export function getStrapiMediaUrl(url?: string | null): string | null {
  if (!url || typeof url !== 'string' || !url.trim()) return null;
  const trimmed = url.trim();

  const publicBase = (process.env.NEXT_PUBLIC_STRAPI_URL || 'https://cms.astropredict.org').replace(/\/$/, '');
  const securePublicBase = publicBase.includes('cms.astropredict.org')
    ? publicBase.replace(/^http:\/\//, 'https://')
    : publicBase;

  // Internal Docker hostname rewrite (e.g. http://strapi:1337/uploads/xyz.jpg or strapi:1337/...)
  if (trimmed.includes('strapi:1337')) {
    const path = trimmed.replace(/^https?:\/\/strapi:1337/, '');
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${securePublicBase}${cleanPath}`;
  }

  // Force https for cms.astropredict.org
  if (trimmed.startsWith('http://cms.astropredict.org')) {
    return trimmed.replace('http://cms.astropredict.org', 'https://cms.astropredict.org');
  }

  // If already an absolute external URL
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  // Relative path (e.g. /uploads/xyz.jpg or uploads/xyz.jpg)
  const cleanPath = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return `${securePublicBase}${cleanPath}`;
}

// Helper to ensure timeout signal works in Node, Edge, and browser/test environments
export function getTimeoutSignal(ms = 8000): AbortSignal {
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
    return AbortSignal.timeout(ms);
  }
  const controller = new AbortController();
  const timer = setTimeout(() => {
    controller.abort(new DOMException('The operation was aborted due to timeout', 'TimeoutError'));
  }, ms);
  if (typeof (timer as any)?.unref === 'function') {
    (timer as any).unref();
  }
  return controller.signal;
}

export async function getAllBlogPosts(page = 1, pageSize = 25): Promise<StrapiListResponse<StrapiBlogPost>> {
  try {
    const res = await fetch(
      `${STRAPI_BASE_URL}/api/blog-posts?populate=*&pagination[page]=${page}&pagination[pageSize]=${pageSize}&sort=published_date:desc`,
      { next: { revalidate: 60 }, signal: getTimeoutSignal(8000) }
    );
    if (!res.ok) {
      console.warn(`Strapi returned status ${res.status} when fetching blog posts`);
      return { data: [] };
    }
    return await res.json();
  } catch (error: any) {
    if (error?.digest === 'DYNAMIC_SERVER_USAGE') {
      throw error;
    }
    console.warn('Failed to fetch blog posts from Strapi:', error);
    return { data: [] };
  }
}

export async function getBlogPostBySlug(slug: string): Promise<StrapiBlogPost | null> {
  try {
    const res = await fetch(
      `${STRAPI_BASE_URL}/api/blog-posts?filters[slug][$eq]=${encodeURIComponent(slug)}&populate=*`,
      { next: { revalidate: 60 }, signal: getTimeoutSignal(8000) }
    );
    if (!res.ok) {
      console.warn(`Strapi returned status ${res.status} when fetching post by slug: ${slug}`);
      return null;
    }
    const json: StrapiListResponse<StrapiBlogPost> = await res.json();
    if (json.data && json.data.length > 0) {
      return json.data[0];
    }
    return null;
  } catch (error: any) {
    if (error?.digest === 'DYNAMIC_SERVER_USAGE') {
      throw error;
    }
    console.warn(`Failed to fetch post by slug (${slug}) from Strapi:`, error);
    return null;
  }
}

export async function getAllCategories(): Promise<StrapiListResponse<StrapiCategory>> {
  try {
    const res = await fetch(`${STRAPI_BASE_URL}/api/categories?sort=name:asc`, {
      next: { revalidate: 60 },
      signal: getTimeoutSignal(8000)
    });
    if (!res.ok) {
      return { data: [] };
    }
    return await res.json();
  } catch (error: any) {
    if (error?.digest === 'DYNAMIC_SERVER_USAGE') {
      throw error;
    }
    console.warn('Failed to fetch categories from Strapi:', error);
    return { data: [] };
  }
}


